import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MealPlanDto, MealSlot } from '@fewerbags/schema';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { formatDayLong } from '../../lib/planDates';
import {
  cookedConfirmLabel,
  cookedSelectionIssues,
  discardHelpText,
  initialCookedSelections,
  isLeftovers,
  leftoversOffer,
  leftoversOfferLabel,
  leftoversWindow,
  SLOT_OPTIONS,
  toMarkCookedRequest,
  type CookedSelection,
} from '../../lib/plans';
import { unitLabel } from '../../lib/units';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { SelectField } from '../SelectField';

/** What happened to the optional leftovers meal when the plan was marked cooked. */
export type LeftoversOutcome = 'none' | 'saved' | 'failed';

/**
 * "Mark cooked" (D10): the pantry decrement is always proposed and confirmed,
 * never silent. Amounts stay editable and anything unticked is left alone.
 * Each row can also "discard the rest" (D35 F3) — pre-ticked for opened
 * packages that do not keep — which uses the whole row up as waste.
 * When the plan makes more servings than it feeds, a ticked-by-default box
 * also saves the rest as a leftovers meal (PLAN §9 #7), next day same slot.
 */
export function MarkCookedDialog({
  open,
  onClose,
  planId,
  plan,
  onCooked,
}: {
  open: boolean;
  onClose: () => void;
  planId: string;
  /** The plan being cooked, for the leftovers offer; omit to skip the offer. */
  plan?: MealPlanDto | undefined;
  onCooked: (leftovers: LeftoversOutcome) => void;
}) {
  const qc = useQueryClient();
  const [selections, setSelections] = useState<CookedSelection[]>([]);
  const leftoversMeal = plan ? isLeftovers(plan) : false;
  // Plans in the following week, so leftovers already saved are not offered twice.
  const nearbyRange = plan && !leftoversMeal ? leftoversWindow(plan) : null;
  const nearby = useQuery({
    queryKey: queryKeys.plans.week(nearbyRange?.from ?? '', nearbyRange?.to ?? ''),
    queryFn: () => api.plans.list(nearbyRange!.from, nearbyRange!.to),
    enabled: open && nearbyRange !== null,
  });
  const offer = plan && nearby.data ? leftoversOffer(plan, nearby.data.plans) : null;
  const [saveLeftovers, setSaveLeftovers] = useState(true);
  const [leftoversDate, setLeftoversDate] = useState('');
  const [leftoversSlot, setLeftoversSlot] = useState<MealSlot>('dinner');
  // Default target once the offer is known: next day, same slot.
  const offerDate = offer?.date;
  const offerSlot = offer?.slot;
  useEffect(() => {
    if (!offerDate || !offerSlot || leftoversDate) return;
    setLeftoversDate(offerDate);
    setLeftoversSlot(offerSlot);
  }, [offerDate, offerSlot, leftoversDate]);

  const preview = useQuery({
    queryKey: queryKeys.plans.cookedPreview(planId),
    queryFn: () => api.plans.cookedPreview(planId),
    enabled: open && planId !== '',
    staleTime: 0,
  });

  useEffect(() => {
    if (preview.data) setSelections(initialCookedSelections(preview.data));
  }, [preview.data]);

  const detailKey = queryKeys.plans.detail(planId);
  const markCooked = useMutation({
    mutationFn: async () => {
      const updated = await api.plans.markCooked(planId, toMarkCookedRequest(selections));
      // The meal is cooked either way; a failed leftovers save is reported, not rolled back.
      let leftovers: LeftoversOutcome = 'none';
      if (offer && saveLeftovers && leftoversDate) {
        try {
          await api.plans.createLeftovers(planId, {
            date: leftoversDate,
            slot: leftoversSlot,
            servings: offer.servings,
          });
          leftovers = 'saved';
        } catch {
          leftovers = 'failed';
        }
      }
      return { updated, leftovers };
    },
    // Optimistic: the plan flips to "cooked" immediately (the pantry decrement
    // itself is not guessed at here — it's confirmed server-side and the
    // pantry queries are invalidated once that response is back).
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: detailKey });
      const previous = qc.getQueryData<MealPlanDto>(detailKey);
      qc.setQueryData<MealPlanDto>(detailKey, (old) => (old ? { ...old, status: 'cooked' } : old));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(detailKey, ctx.previous);
    },
    onSuccess: async ({ updated, leftovers }) => {
      if (updated) qc.setQueryData(detailKey, updated);
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.plans.all }),
        qc.invalidateQueries({ queryKey: ['pantry'] }),
      ]);
      onCooked(leftovers);
      onClose();
    },
  });

  const issues = cookedSelectionIssues(selections);
  const issueFor = (pantryItemId: string) =>
    issues.find((i) => i.pantryItemId === pantryItemId)?.message;

  function update(pantryItemId: string, patch: Partial<CookedSelection>) {
    setSelections((rows) =>
      rows.map((row) => (row.pantryItemId === pantryItemId ? { ...row, ...patch } : row)),
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title="Mark this meal cooked">
      {preview.isPending ? <p className="text-sm text-muted">Checking the pantry…</p> : null}
      {preview.isError ? <Alert tone="error">{messageForError(preview.error)}</Alert> : null}

      {preview.isSuccess && selections.length === 0 ? (
        <p className="text-sm text-muted">
          {leftoversMeal
            ? 'Leftovers use nothing new from the pantry.'
            : 'Nothing on this meal is in the pantry, so nothing will be used up.'}
        </p>
      ) : null}

      {selections.length > 0 ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-ink">Use up from the pantry</legend>
          <ul className="flex flex-col divide-y divide-line rounded-lg border border-line">
            {selections.map((row) => {
              const checkboxId = `cooked-${row.pantryItemId}`;
              const discardId = `cooked-discard-${row.pantryItemId}`;
              const error = issueFor(row.pantryItemId);
              const discardHelp = row.checked ? discardHelpText(row) : null;
              return (
                <li key={row.pantryItemId} className="flex flex-col gap-1 p-2">
                  <div className="flex items-center gap-2">
                    <input
                      id={checkboxId}
                      type="checkbox"
                      checked={row.checked}
                      onChange={(e) => update(row.pantryItemId, { checked: e.target.checked })}
                      className="size-5 accent-brand"
                    />
                    <label htmlFor={checkboxId} className="flex-1 text-sm font-medium text-ink">
                      {row.ingredientName}
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="any"
                      min={0}
                      aria-label={`Amount of ${row.ingredientName} to use`}
                      aria-invalid={error ? true : undefined}
                      value={row.qtyText}
                      onChange={(e) => update(row.pantryItemId, { qtyText: e.target.value })}
                      className={`min-h-11 w-24 rounded-lg border bg-surface px-2 text-right text-base text-ink ${
                        error ? 'border-fail' : 'border-line'
                      }`}
                    />
                    <span className="w-16 text-sm text-muted">{unitLabel(row.unit)}</span>
                  </div>
                  <p className="pl-7 text-xs text-muted">
                    {row.availableQty} {unitLabel(row.unit)} on hand
                    {row.productLabel ? ` · ${row.productLabel}` : ''}
                    {row.note ? ` · ${row.note}` : ''}
                  </p>
                  {row.checked ? (
                    <div className="flex flex-col gap-0.5 pl-7">
                      <div className="flex items-center gap-2">
                        <input
                          id={discardId}
                          type="checkbox"
                          checked={row.discardRemainder}
                          onChange={(e) =>
                            update(row.pantryItemId, { discardRemainder: e.target.checked })
                          }
                          aria-describedby={discardHelp ? `${discardId}-help` : undefined}
                          className="size-4 accent-brand"
                        />
                        <label htmlFor={discardId} className="text-sm text-ink">
                          Discard the rest
                        </label>
                      </div>
                      {discardHelp ? (
                        <p id={`${discardId}-help`} className="pl-6 text-xs text-muted">
                          {discardHelp}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  {error ? (
                    <p role="alert" className="pl-7 text-xs text-fail">
                      {error}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </fieldset>
      ) : null}

      {preview.data && preview.data.missing.length > 0 ? (
        <div>
          <p className="text-sm font-medium text-ink">Not in the pantry</p>
          <p className="text-sm text-muted">
            {preview.data.missing.map((m) => m.ingredientName).join(', ')}
          </p>
        </div>
      ) : null}

      {offer ? (
        <fieldset className="flex flex-col gap-2 rounded-lg border border-line p-2">
          <div className="flex items-center gap-2">
            <input
              id="cooked-save-leftovers"
              type="checkbox"
              checked={saveLeftovers}
              onChange={(e) => setSaveLeftovers(e.target.checked)}
              className="size-5 accent-brand"
            />
            <label htmlFor="cooked-save-leftovers" className="flex-1 text-sm font-medium text-ink">
              {leftoversOfferLabel(
                { ...offer, date: leftoversDate || offer.date, slot: leftoversSlot },
                formatDayLong,
              )}
            </label>
          </div>
          {saveLeftovers ? (
            <div className="grid gap-2 pl-7 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label htmlFor="cooked-leftovers-date" className="text-sm text-ink">
                  Day
                </label>
                <input
                  id="cooked-leftovers-date"
                  type="date"
                  min={plan?.date}
                  value={leftoversDate}
                  onChange={(e) => setLeftoversDate(e.target.value)}
                  className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
                />
              </div>
              <SelectField
                id="cooked-leftovers-slot"
                label="Meal"
                value={leftoversSlot}
                onChange={(e) => setLeftoversSlot(e.target.value as MealSlot)}
                options={SLOT_OPTIONS}
              />
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {markCooked.isError ? <Alert tone="error">{messageForError(markCooked.error)}</Alert> : null}

      <div className="flex gap-2">
        <Button
          onClick={() => markCooked.mutate()}
          disabled={markCooked.isPending || issues.length > 0 || preview.isPending}
        >
          {markCooked.isPending ? 'Saving…' : cookedConfirmLabel(selections)}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={markCooked.isPending}>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
}
